/** Credential grants (BUILD_PLAN section 4) and assignment status for the current user. */
import { z } from 'zod';
import { AccountIdSchema, DeviceIdSchema, GrantIdSchema, OperationIdSchema } from '../ids.js';
import { IsoDateTimeSchema, MicroUsdSchema, NoQuerySchema, defineRoute } from './common.js';
import { AiKeyAssignmentScopeSchema, AiKeyStatusSchema, GRANT_REDEEM_WINDOW_SECONDS, GrantDenialReasonSchema } from './entities.js';

export { GRANT_REDEEM_WINDOW_SECONDS };

export const CredentialGrantCreateRequestSchema = z
  .object({
    deviceId: DeviceIdSchema,
    operationId: OperationIdSchema,
    accountId: AccountIdSchema,
    /** Conservative maximum reservation for this operation, settled after usage is reported. */
    budgetReservationMicroUsd: MicroUsdSchema,
    /** Model requested; must be permitted for the assigned key. */
    model: z.string().max(128).optional(),
  })
  .strict();
export type CredentialGrantCreateRequest = z.infer<typeof CredentialGrantCreateRequestSchema>;

export const CredentialGrantCreateResponseSchema = z.object({
  grantId: GrantIdSchema,
  /** Redemption deadline (60 seconds after issue). */
  redeemBy: IsoDateTimeSchema,
  /** Single-use nonce the device must sign together with grantId and timestamp. */
  nonce: z.string().min(16),
  budgetReservationMicroUsd: MicroUsdSchema,
  budgetRemainingMicroUsd: MicroUsdSchema.nullable(),
});
export type CredentialGrantCreateResponse = z.infer<typeof CredentialGrantCreateResponseSchema>;

/**
 * Proof of possession: Ed25519 signature (base64url) with the registered device key over the
 * UTF-8 string `${grantId}.${nonce}.${ts}` where ts is the ISO timestamp sent alongside.
 */
export const CredentialGrantRedeemRequestSchema = z
  .object({
    deviceId: DeviceIdSchema,
    ts: IsoDateTimeSchema,
    proof: z.string().min(1),
  })
  .strict();
export type CredentialGrantRedeemRequest = z.infer<typeof CredentialGrantRedeemRequestSchema>;

/** Returned exactly once. Response bodies on this route are never logged. */
export const CredentialGrantRedeemResponseSchema = z.object({
  secret: z.string().min(1),
  model: z.string(),
  permittedModels: z.array(z.string()),
  baseUrl: z.string().url(),
  /** The secret must be released from memory at this time at the latest. */
  expiresAt: IsoDateTimeSchema,
  assignmentVersion: z.number().int().positive(),
  maxOutputTokens: z.number().int().positive(),
  priceTableVersion: z.string(),
});
export type CredentialGrantRedeemResponse = z.infer<typeof CredentialGrantRedeemResponseSchema>;

export function grantProofPayload(grantId: string, nonce: string, ts: string): string {
  return `${grantId}.${nonce}.${ts}`;
}

export const BudgetStatusSchema = z.object({
  limitMicroUsd: MicroUsdSchema.nullable(),
  usedMicroUsd: MicroUsdSchema,
  reservedMicroUsd: MicroUsdSchema,
  remainingMicroUsd: MicroUsdSchema.nullable(),
  periodStart: IsoDateTimeSchema,
  periodEnd: IsoDateTimeSchema,
});
export type BudgetStatus = z.infer<typeof BudgetStatusSchema>;

export const AiAssignmentStatusResponseSchema = z.object({
  assignmentReady: z.boolean(),
  keyStatus: AiKeyStatusSchema.nullable(),
  maskedIdentifier: z.string().nullable(),
  /** `organization` when the key in use is shared with every active user; `user` for a personal assignment. */
  assignmentScope: AiKeyAssignmentScopeSchema.nullable().optional(),
  model: z.string().nullable(),
  higherCapabilityModel: z.string().nullable(),
  permittedModels: z.array(z.string()),
  maxOutputTokens: z.number().int().positive().nullable(),
  policyVersion: z.string(),
  budget: BudgetStatusSchema,
  rumiDisabledAccountIds: z.array(AccountIdSchema),
  /** Why no grant would be issued right now, if applicable. */
  blockedReason: GrantDenialReasonSchema.nullable(),
  lastGrantAt: IsoDateTimeSchema.nullable(),
});
export type AiAssignmentStatusResponse = z.infer<typeof AiAssignmentStatusResponseSchema>;

export const GrantParamsSchema = z.object({ grantId: GrantIdSchema });

export const aiRoutes = {
  'ai.grants.create': defineRoute({
    id: 'ai.grants.create',
    method: 'POST',
    path: '/v1/ai/credential-grants',
    auth: 'desktop',
    // Remediation needs the same inference grant as Rumi; administrators hold rumi_code.* without rumi.query.
    capability: ['rumi.query', 'rumi_code.run'],
    requestSchema: CredentialGrantCreateRequestSchema,
    responseSchema: CredentialGrantCreateResponseSchema,
    description: 'Check suspension/device/account/tool permission/active assignment/budget and issue a 60s single-redemption grant.',
  }),
  'ai.grants.redeem': defineRoute({
    id: 'ai.grants.redeem',
    method: 'POST',
    path: '/v1/ai/credential-grants/:grantId/redeem',
    auth: 'desktop',
    capability: ['rumi.query', 'rumi_code.run'],
    paramsSchema: GrantParamsSchema,
    requestSchema: CredentialGrantRedeemRequestSchema,
    responseSchema: CredentialGrantRedeemResponseSchema,
    noBodyLog: true,
    description: 'Redeem a grant once with device proof of possession; second redemption returns 409.',
  }),
  'ai.assignment.status': defineRoute({
    id: 'ai.assignment.status',
    method: 'GET',
    path: '/v1/ai/assignment',
    auth: 'desktop',
    requestSchema: NoQuerySchema,
    responseSchema: AiAssignmentStatusResponseSchema,
    description: "Current user's inference assignment readiness, model configuration and budget (no secret).",
  }),
} as const;
